import { useEffect, useRef, useState } from 'react';
import { isNil } from 'lodash';

import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';

/**
 * Refetches what a kind of entry is about when such an entry lands. claims-ai publishes the entry
 * once the change it narrates has committed, so a read sent after the entry arrived finds it.
 *
 * An entry is marked checked only once a read this hook sent *for it* has succeeded — never off a
 * read that may have left before the entry arrived. That includes the first read, so the matching
 * entries of the first snapshot cost one read once the first read has settled, landed or failed.
 * One refetch at a time: an entry that arrives while one is in flight waits for it to land and gets
 * its own. A read that fails marks nothing and sends nothing more, since retrying off its own
 * failure would loop against a route that is down: its entries, and any that arrived while it was
 * in flight, are asked for again when the stream next changes.
 */
export const useRefetchForNewEntries = <Response extends object>(
  notifications: StpNotification[],
  /** Whether a read is owed for the entry. Held stable, as a module-scope function: the effect depends on it. */
  isRelevant: (notification: StpNotification) => boolean,
  /** The first read has settled, landed or failed, and the stream has its first snapshot. */
  isReady: boolean,
  /**
   * `reloadData`: resolves to the response, or to `undefined` when the read failed; it never
   * rejects. Typed to the response so a callback that resolves to `void` does not compile: every
   * read would look failed, and every entry would be read for again after each one.
   */
  refetch: () => Promise<Response | undefined>
): void => {
  const checkedEventIds = useRef(new Set<string>());
  const isRefetching = useRef(false);
  // Bumped when a refetch succeeds, so entries that arrived while it was in flight get theirs.
  const [landedCount, setLandedCount] = useState(0);

  useEffect(() => {
    if (!isReady || isRefetching.current) return;

    const checked = checkedEventIds.current;
    const requestedEventIds = notifications
      .filter((notification) => isRelevant(notification) && !checked.has(notification.event_id))
      .map((notification) => notification.event_id);
    if (!requestedEventIds.length) return;

    isRefetching.current = true;
    refetch().then((response) => {
      isRefetching.current = false;
      if (isNil(response)) return;

      requestedEventIds.forEach((eventId) => checked.add(eventId));
      setLandedCount((count) => count + 1);
    });
  }, [notifications, isRelevant, isReady, refetch, landedCount]);
};
